import React, { useState,useEffect,createRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import LockIcon from "@mui/icons-material/Lock";
import {
  Grid,
  Button,
  LinearProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/DevicesOutlined";
import VoicemailIcon from "@mui/icons-material/VoicemailOutlined";
import FaxIcon from "@mui/icons-material/FaxOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import SettingsCard from "./settingsCard";
import ToggleButtonGroup from "./formikToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import PasswordVisibility from "./passwordVisibility";

const useStyles = makeStyles()((theme) => ({
  root: {
    maxWidth: 980,
    margin: "0 auto",
  },
  // A label with a value or a control at the end of the line.
  line: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    minHeight: 38,
  },
  lineLabel: {
    fontSize: 14.5,
    color: theme.palette.text.primary,
  },
  lineHint: {
    fontSize: 12.5,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  lineValue: {
    fontSize: 14.5,
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.primary,
  },
  toggle: {
    "& .MuiToggleButton-root": {
      padding: theme.spacing(0.5, 1.75),
      fontSize: 13,
      textTransform: "none",
      color: theme.palette.text.secondary,
      backgroundColor: "transparent",
      borderColor: theme.palette.divider,
    },
    "& .MuiToggleButton-root:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
      color: "#fff",
      backgroundColor: theme.palette.primary.light,
    },
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: theme.spacing(1),
  },
  progress: {
    alignSelf: "stretch",
  },
  updateButton: {
    textTransform: "none",
    minWidth: 128,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
  },
}));

const AdditionalSettings = (props) => {
  const {
    session,
    user,
    updateUserProfile,
    setUser,
    getUserProfileData,
  } = props;
  const { 
    preferredDevice,
    preferredDeviceNumbers,
    voicemail,
    fax,
    conversation,
    incomingProviderNumber,
    webapp,
  } = user
  const { classes } = useStyles()
  const passRef = createRef()
  const [timeZone, setTimeZone] = useState(user.timeZone)
  const [faxFormat, setFaxFormat] = useState(fax.format)
  const [devicePreference, setDevicePreference] = useState(preferredDevice)
  const [showPassword, setShowPassword] = useState(false)
  const [formChanged, setFormChanged] = useState(false)
  const [faxEnabled, setFaxEnabled] = useState(fax.active)
  const [voicemailEnabled, setVoicemailEnabled] = useState(voicemail.active)
  const [conversationEnabled, setConversationEnabled] = useState(conversation.active)
  const [mobile, setMobile] = useState(preferredDeviceNumbers.mobile)
  const [custom, setCustom] = useState(preferredDeviceNumbers.custom)
  const [clickToDial, setClickToDial] = useState(webapp.features.clickToDial.autoDial)
  // const [incomingProviderNumberValue, setincomingProviderNumberValue] = useState(incomingProviderNumber.number);

  // eslint-disable-next-line
  useEffect(() => {
    if (
      preferredDevice !== devicePreference ||
      fax.format !== faxFormat ||
      user.timeZone !== timeZone ||
      fax.active !== faxEnabled ||
      voicemail.active !== voicemailEnabled ||
      conversation.active !== conversationEnabled ||
      mobile !== preferredDeviceNumbers.mobile ||
      custom !== preferredDeviceNumbers.custom || 
      clickToDial !== webapp.features.clickToDial.autoDial // ||
      // incomingProviderNumberValue !== incomingProviderNumber.number
    ) {
      setFormChanged(true);
    } 
    else {
      setFormChanged(false);
    }
  });

  const handleValidation = (values) => {
    const errors = {};

    return errors;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    let data = {
      token: session.token,
      server: session.lastServer,
      userProfile: {
        firstName: user.firstName,
        lastName: user.lastName,
        preferredDevice: devicePreference,
        timeZone: timeZone,
        preferredDeviceNumbers: {
          mobile: mobile,
          custom: custom,
        },
        voicemail: {
          active: voicemailEnabled,
        },
        fax: {
          email: values.faxEmail,
          active: faxEnabled,
          format: faxFormat,
        },
        conversation: {
          active: conversationEnabled,
        },
        incomingProviderNumber: incomingProviderNumber,
        clickToDial: {
          autoDial: clickToDial
        }
      },
    };

    try {
      await updateUserProfile(data);

      setSubmitting(false);

      try {
        await getUserProfileData(
          {
            token: session.token,
            server: session.lastServer,
          },
          setUser
        );
      } catch (error) {
        setSubmitting(false);
        console.error(error);
      }
    } catch (error) {
      setSubmitting(false);
      console.error(error);
    }
  };

  const generateTimeZoneOptions = () => {
    let timeZones = [
      "UTC-12",
      "UTC-11",
      "UTC-10",
      "UTC-9",
      "UTC-8",
      "UTC-7",
      "UTC-6",
      "UTC-5",
      "UTC-4",
      "UTC-3",
      "UTC-2",
      "UTC-1",
      "UTC-0",
      "UTC+1",
      "UTC+2",
      "UTC+3",
      "UTC+4",
      "UTC+5",
      "UTC+6",
      "UTC+7",
      "UTC+8",
      "UTC+9",
      "UTC+10",
      "UTC+11",
      "UTC+12",
    ];
    return timeZones.map((item) => (
      <MenuItem key={item} value={item}>
        {item}
      </MenuItem>
    ));
  };

  return (
    <Formik
      initialValues={{
        office: preferredDeviceNumbers.office,
        mobile: preferredDeviceNumbers.mobile,
        custom: preferredDeviceNumbers.custom,
        incomingProviderNumber: incomingProviderNumber.number,
        smsNumber: conversation.number,
        voicemailEnabled: voicemail.active,
        vacationMode: false,
        voicemailPassword: voicemail.secret,
        faxEnabled: fax.active,
        faxDeliverAs: fax.format,
        faxEmail: fax.email,
        faxNumber: fax.number,
        clickToDial: webapp.features.clickToDial.autoDial,
      }}
      validate={handleValidation}
      onSubmit={(values, setSubmitting) => handleSubmit(values, setSubmitting)}
    >
      {({ submitForm, isSubmitting }) => (
        <Form onSubmit={(e) => e.preventDefault()}>
          <Grid
            container
            className={classes.root}
            spacing={3}
            sx={{
              alignItems: "stretch",
            }}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <SettingsCard
                icon={<DevicesIcon />}
                title="My devices"
                hint="Where your calls ring."
              >
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  <InputLabel id="preferred-device-label">
                    Preferred device
                  </InputLabel>
                  <Select
                    labelId="preferred-device-label"
                    id="preferred-device"
                    value={devicePreference}
                    label="Preferred device"
                    name="preferredDevice"
                    onChange={(e) => setDevicePreference(e.target.value)}
                  >
                    <MenuItem value="office">Office</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  disabled
                  name="office"
                  type="text"
                  label="Office"
                  placeholder="Not set"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" fontSize="small" />
                      </InputAdornment>
                    ),
                  } }}
                />
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    value: mobile,
                    onChange: (e) => setMobile(e.target.value),
                  } }}
                  name="mobile"
                  type="text"
                  label="Mobile"
                  placeholder="Add mobile number"
                />
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    value: custom,
                    onChange: (e) => setCustom(e.target.value),
                  } }}
                  name="custom"
                  type="text"
                  label="Custom"
                  placeholder="Add another number"
                />
              </SettingsCard>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <SettingsCard
                icon={<VoicemailIcon />}
                title="Voicemail"
                hint={voicemail.mailbox ? `Mailbox ${voicemail.mailbox}` : undefined}
                action={
                  <Switch
                    color="primary"
                    checked={voicemailEnabled === "yes" ? true : false}
                    name="voicemailEnabled"
                    onChange={(e) =>
                      setVoicemailEnabled(e.target.checked ? "yes" : "no")
                    }
                    slotProps={{ input: { "aria-label": "Enable Voicemail" } }}
                  />
                }
              >
                <div className={classes.line}>
                  <div>
                    <div className={classes.lineLabel}>Vacation mode</div>
                    <div className={classes.lineHint}>Not available yet.</div>
                  </div>
                  <Switch
                    disabled={true}
                    name="vacationMode"
                    slotProps={{ input: { "aria-label": "Enable Vacation Mode" } }}
                  />
                </div>
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  <InputLabel id="timeZone-label">
                    Time zone
                  </InputLabel>
                  <Select
                    labelId="timeZone-label"
                    id="timeZone"
                    value={timeZone}
                    label="Time zone"
                    name="timeZone"
                    onChange={(e) => setTimeZone(e.target.value)}
                  >
                    {generateTimeZoneOptions()}
                  </Select>
                </FormControl>
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  name="voicemailPassword"
                  type="password"
                  label="Voicemail password"
                  placeholder="Not set"
                  autoComplete="voicemail-password"
                  disabled
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    endAdornment: (
                      <PasswordVisibility
                        showPassword={showPassword}
                        togglePasswordVisibility={setShowPassword}
                        passRef={passRef}
                      />
                    ),
                    inputRef: passRef,
                  } }}
                />
              </SettingsCard>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <SettingsCard
                icon={<FaxIcon />}
                title="Fax"
                hint="Incoming faxes are sent to your e-mail."
                action={
                  <Switch
                    color="primary"
                    name="faxEnabled"
                    checked={faxEnabled === "yes" ? true : false}
                    onChange={(e) =>
                      setFaxEnabled(e.target.checked ? "yes" : "no")
                    }
                    slotProps={{ input: { "aria-label": "Enable Fax" } }}
                  />
                }
              >
                <div className={classes.line}>
                  <div className={classes.lineLabel}>Deliver as</div>
                  <Field
                    component={ToggleButtonGroup}
                    value={faxFormat}
                    name="faxDeliverAs"
                    exclusive
                    size="small"
                    aria-label="fax delivery format"
                    className={classes.toggle}
                    onChange={(e, newValue) => setFaxFormat(newValue)}
                  >
                    <ToggleButton value="PDF" aria-label="PDF">
                      PDF
                    </ToggleButton>
                    <ToggleButton value="TIF" aria-label="TIF">
                      TIF
                    </ToggleButton>
                    <ToggleButton value="PDF,TIF" aria-label="PDF and TIF">
                      Both
                    </ToggleButton>
                  </Field>
                </div>
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  disabled
                  name="faxEmail"
                  type="email"
                  label="Fax e-mail"
                  placeholder="Not set"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" fontSize="small" />
                      </InputAdornment>
                    ),
                  } }}
                />
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  disabled
                  name="faxNumber"
                  type="text"
                  label="Fax number"
                  placeholder="Not set"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" fontSize="small" />
                      </InputAdornment>
                    ),
                  } }}
                />
              </SettingsCard>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <SettingsCard
                icon={<SmsIcon />}
                title="Conversation"
                hint="SMS messaging and dialing."
                action={
                  <Switch
                    color="primary"
                    name="conversationEnabled"
                    checked={conversationEnabled === "yes" ? true : false}
                    onChange={(e) =>
                      setConversationEnabled(e.target.checked ? "yes" : "no")
                    }
                    slotProps={{ input: { "aria-label": "Enable Conversation" } }}
                  />
                }
              >
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  disabled
                  name="smsNumber"
                  type="text"
                  label="SMS number"
                  placeholder="Not set"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" fontSize="small" />
                      </InputAdornment>
                    ),
                  } }}
                />
                <Field
                  variant="outlined"
                  component={TextField}
                  fullWidth
                  size="small"
                  disabled
                  name="incomingProviderNumber"
                  type="text"
                  label="Incoming provider number"
                  placeholder="Not set"
                  slotProps={{ inputLabel: { shrink: true }, input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon color="disabled" fontSize="small" />
                      </InputAdornment>
                    ),
                  } }}
                />
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  <InputLabel id="click-to-dial-label">
                    Click-to-dial
                  </InputLabel>
                  <Select
                    labelId="click-to-dial-label"
                    id="click-to-dial"
                    value={clickToDial}
                    label="Click-to-dial"
                    name="clickToDial"
                    onChange={(e) => setClickToDial(e.target.value)}
                  >
                    <MenuItem value="yes">Auto dial on</MenuItem>
                    <MenuItem value="no">Auto dial off</MenuItem>
                  </Select>
                </FormControl>
              </SettingsCard>
            </Grid>
            <Grid className={classes.footer} size={12}>
              {isSubmitting && <LinearProgress className={classes.progress} />}
              <Button
                variant="contained"
                disabled={isSubmitting || !formChanged}
                onClick={submitForm}
                className={classes.updateButton}
              >
                {isSubmitting ? "Updating…" : "Update"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

AdditionalSettings.propTypes = {
  user: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
  setUser: PropTypes.func.isRequired,
  updateUserProfile: PropTypes.func.isRequired,
  getUserProfileData: PropTypes.func.isRequired,
};

export default AdditionalSettings;
